import * as THREE from 'three';
import {
  BOARD_CONDITIONS,
  CONDITION_BOARD_EDGE,
  getIceTileIndices,
  getMagnetPosition,
} from './boardConditions';

const material = (color, opacity = 0.7) =>
  new THREE.LineBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
    toneMapped: false,
  });
const makeLine = (points, color, opacity) =>
  new THREE.Line(
    new THREE.BufferGeometry().setFromPoints(
      points.map((point) => new THREE.Vector3(...point))
    ),
    material(color, opacity)
  );
const glow = (color, opacity = 0.7) =>
  new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });

export const createBoardConditionScene = (board, surfaceY) => {
  const root = new THREE.Group();
  board.add(root);
  let signature = '';
  let particles = [];
  let rings = [];
  const clear = () => {
    root.traverse((object) => {
      object.geometry?.dispose();
      object.material?.dispose();
    });
    root.clear();
    particles = [];
    rings = [];
  };
  const arrow = (x, z, direction, color) => {
    root.add(
      makeLine(
        [
          [x - direction * 0.5, surfaceY + 0.07, z],
          [x + direction * 0.5, surfaceY + 0.07, z],
          [x + direction * 0.15, surfaceY + 0.07, z - 0.24],
        ],
        color,
        0.85
      )
    );
    root.add(
      makeLine(
        [
          [x + direction * 0.5, surfaceY + 0.07, z],
          [x + direction * 0.15, surfaceY + 0.07, z + 0.24],
        ],
        color,
        0.85
      )
    );
  };
  const configure = (condition) => {
    clear();
    if (!condition) return;
    const color = BOARD_CONDITIONS[condition.type]?.color || '#74eaff';
    if (condition.type === 'crosswind') {
      [-7.3, 7.3].forEach((z) =>
        [-5, -2.5, 0, 2.5, 5].forEach((x) =>
          arrow(x, z, condition.direction, color)
        )
      );
      particles = Array.from({ length: 28 }, (_, index) => {
        const streak = makeLine(
          [
            [0, 0, 0],
            [0.35 + (index % 3) * 0.16, 0, 0],
          ],
          color,
          0.25
        );
        root.add(streak);
        return streak;
      });
    } else if (condition.type === 'magnetic') {
      const anchor = getMagnetPosition(condition);
      const magnet = new THREE.Group();
      magnet.position.set(anchor.x, surfaceY + 0.28, anchor.z);
      const arc = new THREE.Mesh(
        new THREE.TorusGeometry(0.48, 0.11, 8, 24, Math.PI),
        glow(color, 0.8)
      );
      arc.rotation.x = -Math.PI / 2;
      magnet.add(arc);
      [-1, 1].forEach((side) => {
        const pole = new THREE.Mesh(
          new THREE.BoxGeometry(0.22, 0.2, 0.4),
          glow(side === 1 ? '#96faff' : color)
        );
        pole.position.set(side * 0.48, 0, 0.2);
        magnet.add(pole);
      });
      magnet.rotation.y = (condition.direction * Math.PI) / 2;
      root.add(magnet);
      rings = [2.5, 4.8, 7].map((radius) => {
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(radius, 0.012, 4, 80),
          glow(color, 0.12)
        );
        ring.rotation.x = -Math.PI / 2;
        ring.position.set(anchor.x, surfaceY + 0.03, anchor.z);
        root.add(ring);
        return ring;
      });
      [-5.5, -2.5, 2.5, 5.5].forEach((z) => {
        const curve = new THREE.QuadraticBezierCurve3(
          new THREE.Vector3(-condition.direction * 3.5, surfaceY + 0.035, z),
          new THREE.Vector3(condition.direction * 4, surfaceY + 0.035, z),
          new THREE.Vector3(anchor.x, surfaceY + 0.035, anchor.z)
        );
        root.add(
          new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(curve.getPoints(32)),
            material(color, 0.16)
          )
        );
      });
    } else if (condition.type === 'ice') {
      getIceTileIndices(condition).forEach((index) => {
        const x = ((index % 5) - 2) * 2.85;
        const z = (Math.floor(index / 5) - 2) * 2.85;
        const y = surfaceY + 0.03;
        root.add(
          makeLine(
            [
              [x - 1.27, y, z - 1.27],
              [x + 1.27, y, z - 1.27],
              [x + 1.27, y, z + 1.27],
              [x - 1.27, y, z + 1.27],
              [x - 1.27, y, z - 1.27],
            ],
            color,
            0.95
          )
        );
        [-1, 1].forEach((side) => {
          root.add(
            makeLine(
              [
                [x + side * 1.1, y, z - 1.1],
                [x + side * 0.86, y, z - 0.78],
                [x + side * 1.16, y, z - 0.56],
              ],
              '#efffff',
              0.75
            )
          );
          root.add(
            makeLine(
              [
                [x + side * 1.06, y, z + 0.97],
                [x + side * 0.78, y, z + 0.75],
              ],
              color,
              0.6
            )
          );
        });
      });
    } else if (condition.type === 'lowGravity') {
      particles = Array.from({ length: 24 }, () => {
        const spark = makeLine(
          [
            [0, 0, 0],
            [0, 0.25, 0],
          ],
          color,
          0.45
        );
        root.add(spark);
        return spark;
      });
      [-1, 1].forEach((x) =>
        [-1, 1].forEach((z) => {
          const ring = new THREE.Mesh(
            new THREE.TorusGeometry(0.38, 0.016, 5, 32),
            glow(color, 0.65)
          );
          ring.rotation.x = -Math.PI / 2;
          ring.position.set(x * 7.25, surfaceY + 0.3, z * 7.25);
          root.add(ring);
          rings.push(ring);
        })
      );
    }
  };
  const update = (condition, { time, moving, reduceMotion }) => {
    const nextSignature = condition
      ? `${condition.type}:${condition.direction}:${condition.lane}:${condition.laneAxis}`
      : '';
    if (nextSignature !== signature) {
      signature = nextSignature;
      configure(condition);
    }
    root.visible = !!condition;
    if (!condition) return;
    const seconds = reduceMotion || !moving ? 0 : time;
    if (condition.type === 'crosswind')
      particles.forEach((particle, index) => {
        const travel =
          ((seconds * 0.2 + index / particles.length) % 1) *
          CONDITION_BOARD_EDGE *
          2;
        particle.position.set(
          condition.direction * (travel - CONDITION_BOARD_EDGE),
          surfaceY + 0.16 + (index % 3) * 0.11,
          ((index * 2.17) % 13.4) - 6.7
        );
        particle.material.opacity = reduceMotion
          ? 0.08
          : 0.15 + Math.sin((travel / 14) * Math.PI) * 0.1;
      });
    if (condition.type === 'magnetic')
      rings.forEach((ring, index) => {
        ring.material.opacity =
          0.1 +
          (reduceMotion
            ? 0
            : (1 + Math.sin(seconds * 2.5 - index * 1.8)) * 0.035);
      });
    if (condition.type === 'lowGravity') {
      particles.forEach((particle, index) => {
        particle.position.set(
          (index % 2 ? -1 : 1) * (7.1 + (index % 3) * 0.18),
          surfaceY + ((seconds * 0.7 + index * 0.17) % 2.6),
          -6.5 + ((index * 1.91) % 13)
        );
      });
      rings.forEach((ring, index) => {
        ring.position.y =
          surfaceY + 0.4 + Math.sin(seconds * 1.3 + index) * 0.18;
      });
    }
  };
  return { root, update };
};
